import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { get_diseases } from "../tools-network";
import { showTemporaryMessage } from "../components/Message";

const MultiDiseaseSelector = ({ loginToken, initialDiseases = [], onDiseasesChange, onEditingChange, onSave, disabled = false }) => {
	const { t } = useTranslation();
	const [diseases, setDiseases] = useState(initialDiseases);
	const [availableDiseases, setAvailableDiseases] = useState([]);
	const [flatDiseaseList, setFlatDiseaseList] = useState([]);
	
	// Two-level editing states
	const [isInOverviewEditMode, setIsInOverviewEditMode] = useState(false);
	const [editingIndex, setEditingIndex] = useState(null);
	const [showAddForm, setShowAddForm] = useState(false);
	
	// Add form states
	const [addFormDiseaseText, setAddFormDiseaseText] = useState('');
	const [addFormUserDetails, setAddFormUserDetails] = useState('');
	
	// Edit form states
	const [editDiseaseText, setEditDiseaseText] = useState('');
	const [editUserDetails, setEditUserDetails] = useState('');
	const [originalDiseaseText, setOriginalDiseaseText] = useState('');
	const [originalUserDetails, setOriginalUserDetails] = useState('');
	
	// Delete confirmation states
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleteIndex, setDeleteIndex] = useState(null);
	const [deleteConfirmName, setDeleteConfirmName] = useState('');

	useEffect(() => {
		loadAvailableDiseases();
	}, []);

	// Sync local diseases state with prop changes
	useEffect(() => {
		setDiseases(initialDiseases);
	}, [initialDiseases]);

	// Report editing state changes to parent
	useEffect(() => {
		if (onEditingChange) {
			onEditingChange(isInOverviewEditMode || showAddForm || editingIndex !== null);
		}
	}, [isInOverviewEditMode, showAddForm, editingIndex, onEditingChange]);

	const loadAvailableDiseases = async () => {
		try {
			const res = await get_diseases(loginToken);
			if (res.status) {
				setAvailableDiseases(res.data);
				// Create flat list with hierarchy indication
				const flat = flattenDiseases(res.data);
				setFlatDiseaseList(flat);
			} else {
				showTemporaryMessage("Fetch diseases FAILED!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Fetch diseases FAILED!", err, 'danger');
		}
	};

	const flattenDiseases = (data) => {
		const map = {};
		data.forEach((item) => {
			map[item.id] = { 
				id: item.id,
				name: item.name,
				prioritized: item.prioritized || false,
				parent_id: item.parent_id,
				children: [] 
			};
		});

		const tree = [];
		data.forEach((item) => {
			if (item.parent_id === null) {
				tree.push(map[item.id]);
			} else {
				if (map[item.parent_id]) {
					map[item.parent_id].children.push(map[item.id]);
				}
			}
		});

		// Flatten with indentation and sort: prioritized first, then alphabetically
		const flat = [];
		const traverse = (nodes, depth = 0) => {
			// Sort nodes: prioritized first, then alphabetically
			const sortedNodes = [...nodes].sort((a, b) => {
				// Prioritized items come first
				if (a.prioritized && !b.prioritized) return -1;
				if (!a.prioritized && b.prioritized) return 1;
				// Within same priority level, sort alphabetically
				return (a.name || '').localeCompare(b.name || '');
			});
			
			sortedNodes.forEach(node => {
				flat.push({ id: node.id, name: node.name, prioritized: node.prioritized, depth });
				if (node.children && node.children.length > 0) {
					traverse(node.children, depth + 1);
				}
			});
		};
		traverse(tree);
		
		// Move "övrigt" to the end of the entire list
		const ovrigtIndex = flat.findIndex(item => (item.name || '').toLowerCase() === 'övrigt');
		if (ovrigtIndex !== -1) {
			const ovrigtItem = flat.splice(ovrigtIndex, 1)[0];
			flat.push(ovrigtItem);
		}
		
		return flat;
	};

	const addDisease = () => {
		if (diseases.length < 3) {
			const newDiseases = [...diseases, { disease_text: addFormDiseaseText, user_details: addFormUserDetails }];
			setDiseases(newDiseases);
			onDiseasesChange(newDiseases);
			setShowAddForm(false);
			setAddFormDiseaseText('');
			setAddFormUserDetails('');
		}
	};

	const removeDisease = (index) => {
		const newDiseases = diseases.filter((_, i) => i !== index);
		setDiseases(newDiseases);
		onDiseasesChange(newDiseases);
		setShowDeleteConfirm(false);
		setDeleteIndex(null);
		setDeleteConfirmName('');
	};

	const updateDisease = (index, updates) => {
		const newDiseases = [...diseases];
		newDiseases[index] = { ...newDiseases[index], ...updates };
		setDiseases(newDiseases);
		onDiseasesChange(newDiseases);
	};

	const moveDisease = (index, direction) => {
		const newDiseases = [...diseases];
		const targetIndex = direction === 'up' ? index - 1 : index + 1;
		if (targetIndex >= 0 && targetIndex < diseases.length) {
			[newDiseases[index], newDiseases[targetIndex]] = [newDiseases[targetIndex], newDiseases[index]];
			setDiseases(newDiseases);
			onDiseasesChange(newDiseases);
		}
	};
	
	const startEdit = (index) => {
		const disease = diseases[index];
		setEditingIndex(index);
		setEditDiseaseText(disease.disease_text || '');
		setEditUserDetails(disease.user_details || '');
		setOriginalDiseaseText(disease.disease_text || '');
		setOriginalUserDetails(disease.user_details || '');
	};
	
	const saveEdit = () => {
		updateDisease(editingIndex, {
			disease_text: editDiseaseText,
			user_details: editUserDetails
		});
		cancelEdit();
	};
	
	const cancelEdit = () => {
		setEditingIndex(null);
		setEditDiseaseText('');
		setEditUserDetails('');
		setOriginalDiseaseText('');
		setOriginalUserDetails('');
	};
	
	const handleDelete = (index) => {
		setDeleteIndex(index);
		setShowDeleteConfirm(true);
	};
	
	const confirmDelete = () => {
		const disease = diseases[deleteIndex];
		const expectedName = disease?.disease_text || '';
		
		if (deleteConfirmName !== expectedName) {
			showTemporaryMessage(t('name_does_not_match'), '', 'danger');
			return;
		}
		
		removeDisease(deleteIndex);
	};

	return (
		<div className="w-full space-y-4">
			{/* Display content only when in edit mode */}
			{isInOverviewEditMode && (
				<>
					<div className="flex justify-between items-center">
						<h3 className="text-lg font-semibold">{t("diseases")} ({diseases.length}/3)</h3>
					</div>

					{diseases.length === 0 && (
						<p className="text-gray-500 text-sm text-center py-4">{t("no_diseases_added")}</p>
					)}

			{/* Add new disease form */}
			{showAddForm && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
						<h3 className="text-lg font-bold text-gray-900 mb-4">{t('add_disease')}</h3>
						<div className="space-y-3">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									{t("select_disease")}
								</label>
								<select
									value={addFormDiseaseText}
									onChange={(e) => setAddFormDiseaseText(e.target.value)}
									disabled={disabled}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
									autoFocus
								>
									<option value="">{t("select_from_list")}</option>
									{flatDiseaseList.map((item) => (
										<option key={item.id} value={item.name}>
											{'—'.repeat(item.depth)} {item.name}
										</option>
									))}
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									{t("additional_details")}
								</label>
								<textarea
									value={addFormUserDetails}
									onChange={(e) => setAddFormUserDetails(e.target.value)}
									rows="3"
									placeholder={t("enter_additional_disease_information")}
									disabled={disabled}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
								/>
							</div>
							<div className="flex gap-2 justify-end pt-2">
								<button
									onClick={() => {
										setShowAddForm(false);
										setAddFormDiseaseText('');
										setAddFormUserDetails('');
									}}
									className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition"
								>
									{t('cancel')}
								</button>
								<button
									onClick={addDisease}
									disabled={!addFormDiseaseText.trim()}
									className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
								>
									{t('add_disease')}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Disease list */}
			<div className="space-y-3">
				{diseases.map((disease, index) => (
					<div 
						key={index} 
						className={`border rounded-md p-4 ${index === 0 ? 'border-cyan-500 border-2 bg-cyan-50' : 'border-gray-200'}`}
					>
						{editingIndex === index ? (
							// Individual edit mode
							<div className="space-y-3">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										{t("select_disease")}
									</label>
									<select
										value={editDiseaseText}
										onChange={(e) => setEditDiseaseText(e.target.value)}
										disabled={disabled}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
									>
										<option value="">{t("select_from_list")}</option>
										{flatDiseaseList.map((item) => (
											<option key={item.id} value={item.name}>
												{'—'.repeat(item.depth)} {item.name}
											</option>
										))}
									</select>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										{t("additional_details")}
									</label>
									<textarea
										value={editUserDetails}
										onChange={(e) => setEditUserDetails(e.target.value)}
										rows="3"
										placeholder={t("enter_additional_disease_information")}
										disabled={disabled}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
									/>
								</div>
								<div className="flex gap-2">
									<button
										onClick={saveEdit}
										disabled={editDiseaseText === originalDiseaseText && editUserDetails === originalUserDetails}
										className="px-3 py-1.5 rounded text-sm font-medium transition disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed bg-blue-500 text-white hover:bg-blue-600"
									>
										{t('save_changes')}
									</button>
									<button
										onClick={cancelEdit}
										className="bg-gray-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-gray-600 transition"
									>
										{t('cancel')}
									</button>
								</div>
							</div>
						) : (
							// View mode
							<div>
								<div className="flex justify-between items-start mb-2">
									<div className="flex items-center gap-2">
										<span className={`font-semibold text-sm ${index === 0 ? 'text-cyan-900' : ''}`}>
											{index === 0 && <span className="text-xs font-normal text-cyan-700 mr-1">★</span>}
											{t("disease")} {index + 1}
										</span>
										{index === 0 && <span className="text-xs text-cyan-700 italic">{t("primary_disease")}</span>}
									</div>
								</div>
								<div className="text-sm text-gray-900 mb-2">
									<div className="font-medium">{disease.disease_text || <span className="text-gray-400 italic">{t("no_disease_selected")}</span>}</div>
									{disease.user_details && (
										<div className="text-gray-600 ml-2 mt-1">{disease.user_details}</div>
									)}
								</div>
								
								{/* Overview edit mode actions */}
								{isInOverviewEditMode && (
									<div className="flex gap-2 mt-3 flex-wrap">
										<button
											onClick={() => startEdit(index)}
											disabled={showAddForm}
											className="bg-cyan-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-cyan-600 transition disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
										>
											{t('edit')}
										</button>
										
										{/* Move buttons - only in overview mode */}
										<div className="flex gap-1">
											<button
												onClick={() => moveDisease(index, 'up')}
												disabled={disabled || index === 0}
												title={t("move_up")}
												className="text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 px-2 py-1 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed border border-gray-300"
											>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
												</svg>
											</button>
											<button
												onClick={() => moveDisease(index, 'down')}
												disabled={disabled || index === diseases.length - 1}
												title={t("move_down")}
												className="text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 px-2 py-1 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed border border-gray-300"
											>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
												</svg>
											</button>
										</div>
										
										<button
											onClick={() => handleDelete(index)}
											className="bg-red-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-red-600 transition"
										>
											{t('remove')}
										</button>
									</div>
								)}
							</div>
						)}
					</div>
				))}
			</div>

			{/* Control buttons */}
			<div className="mt-4 flex gap-2">
				{isInOverviewEditMode && !showAddForm && (
					<button
						onClick={async () => {
							if (onSave) {
								await onSave(diseases);
							}
							setIsInOverviewEditMode(false);
						}}
						disabled={editingIndex !== null}
						className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
					>
						{t('done_editing')}
					</button>
				)}
				{diseases.length < 3 && !showAddForm && isInOverviewEditMode && (
					<button
						onClick={() => setShowAddForm(true)}
						disabled={editingIndex !== null || disabled}
						className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
					>
						{t('add_disease')}
					</button>
				)}
			</div>

			{diseases.length >= 3 && (
				<p className="text-gray-500 text-sm mt-2 italic">
					{t('max_diseases_reached')}
				</p>
			)}
				</>
			)}

			{/* Control button when not in edit mode */}
			{!isInOverviewEditMode && !showAddForm && (
				<button
					onClick={() => setIsInOverviewEditMode(true)}
					disabled={disabled}
					className="w-full bg-cyan-500 text-white px-4 py-2 rounded-md hover:bg-cyan-600 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
				>
					{t('edit_diseases')}
				</button>
			)}

			{/* DELETE CONFIRMATION MODAL */}
			{showDeleteConfirm && (() => {
				const disease = diseases[deleteIndex];
				const displayName = disease?.disease_text || '';
				return (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
						<div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
							<h3 className="text-lg font-bold text-gray-900 mb-4">{t('confirm_delete_disease')}</h3>
							<div className="bg-red-50 border-l-4 border-red-600 p-4 mb-4">
								<div className="flex">
									<div>
										<p className="text-sm font-semibold text-red-800">{t('delete_disease_warning_title')}</p>
										<p className="text-sm text-red-700 mt-1">{t('delete_disease_warning_message')}</p>
									</div>
								</div>
							</div>
							<p className="text-sm text-gray-700 mb-4">
								{t('type_disease_name_to_confirm')}: <span className="font-bold">{displayName}</span>
							</p>
							<input
								type="text"
								value={deleteConfirmName}
								onChange={(e) => setDeleteConfirmName(e.target.value)}
								placeholder={t('enter_disease_name')}
								className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-cyan-600"
							/>
							<div className="flex gap-2 justify-end">
								<button
									onClick={() => {
										setShowDeleteConfirm(false);
										setDeleteIndex(null);
										setDeleteConfirmName('');
									}}
									className="bg-gray-500 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-600"
								>
									{t('cancel')}
								</button>
								<button
									onClick={confirmDelete}
									disabled={deleteConfirmName !== displayName}
									className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
								>
									{t('remove')}
								</button>
							</div>
						</div>
					</div>
				);
			})()}
		</div>
	);
};

export default MultiDiseaseSelector;
